
import React, { useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'


import FullPageTitle from '@components/shared/fullpage/FullPageTitle'
import FullPageSection from '@components/shared/fullpage/FullPageSection'
import slugify from '@utils/slugify'

import { useMinScreen } from '@utils/hooks'

import QuoteIcon from '@svg/quote.svg'
import Arrow from '@svg/arrow.svg'

import SwiperCore, { Autoplay, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

SwiperCore.use([Autoplay, Navigation])

interface ClientsProps {
  isDecoupled?: boolean
  color?: string
  titleColor?: string
  textColor?: string
  data?: any
}

const Clients: React.FC<ClientsProps> = ({
  color,
  titleColor,
  textColor,
  isDecoupled,
  data = null
}) => {
  const { depoimentos, clientes } = useStaticQuery(graphql`
    query clientsComponent {
      depoimentos: allWpTestimonial {
        nodes {
          title
          acfDepoimento {
            cargo
          }
          content
        }
      }
      clientes: allWpClient {
        nodes {
          featuredImage {
            node {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    height: 64
                    placeholder: BLURRED
                    layout: CONSTRAINED
                  )
                }
              }
            }
          }
        }
      }
    }
  `)

  const [depoimentoIndex, setDepoimentoIndex] = useState<number>(null)

  const { min } = useMinScreen()

  return (
    <div
      className={`section bg-[#111] bg-[radial-gradient(at_bottom_right,rgba(50,50,50,1)_0%,rgba(0,0,0,1)_80%)] ${
        isDecoupled ? 'py-20' : ''
      }`}
    >
      <FullPageSection
        isTitleCentered
        isDecoupled={isDecoupled}
        title={
          <FullPageTitle tagName="h2" color={titleColor}>
            O que nossos clientes dizem
          </FullPageTitle>
        }
      >
        <div className="flex flex-col h-full">
          <div
            className={`${textColor} flex flex-wrap grow w-full max-w-full lg:px-16 lg:mt-10`}
          >
            <Swiper
              slidesPerView={1}
              spaceBetween={30}
              centeredSlides={true}
              autoplay={{ delay: 6000 }}
              loop={true}
              onSlideChange={e => setDepoimentoIndex(e.realIndex)}
              grabCursor={true}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
              }}
              breakpoints={{
                991: {
                  slidesPerView: 3,
                  spaceBetween: 30
                }
              }}
            >
              {depoimentos &&
                depoimentos.nodes.map(
                  ({ title, acfDepoimento: { cargo }, content }) => (
                    <SwiperSlide key={slugify(title)}>
                      <div className="text-left text-base bg-transparent pt-12 px-2 sm:pt-16 sm:px-4 lg:text-center backface-hidden">
                        <QuoteIcon
                          className="absolute fill-current text-white opacity-12"
                          style={{
                            transform: min`md`
                              ? 'translate3D(-15%, -50%, 0) scale(1)'
                              : 'translate3D(-20%, -60%, 0) scale(0.75)'
                          }}
                        />
                        <div
                          dangerouslySetInnerHTML={{ __html: content }}
                        ></div>
                        <div className="mt-4">
                          <p className="font-bold">{title}</p>
                          <p className="font-light">{cargo}</p>
                        </div>
                      </div>
                    </SwiperSlide>
                  )
                )}
            </Swiper>

            <div className={`flex my-4 lg:hidden`}>
              <div className="swiper-button-prev mr-3">
                <Arrow className="text-white fill-current scale-75 rotate-180 transform" />
              </div>
              <div className="font-sans2 leading-none tracking-widest">
                <span className="text-xl">{depoimentoIndex + 1}</span>
                <span className="text-sm">/6</span>
              </div>
              <div className="swiper-button-next ml-3">
                <Arrow className="text-white fill-current scale-75 transform" />
              </div>
            </div>
          </div>
          <div
            className={`bottom-0 mb-8 text-white container mx-auto ${
              isDecoupled ? 'pt-12 md:pt-24' : 'absolute'
            }`}
          >
            {/* Pseudo-element replacement for separator */}
            <div className="h-1 w-40 rounded-full block m-auto mb-8 md:w-64 md:mb-10 bg-white/30" />

            {min`lg` ? (
              <div className="flex justify-between">
                {clientes &&
                  clientes.nodes.map(({ featuredImage }, index) => (
                    <div key={index}>
                      <div className="flex justify-center items-center h-16 md:h-20 md:w-48">
                        <GatsbyImage
                          image={
                            featuredImage.node.localFile.childImageSharp
                              .gatsbyImageData
                          }
                          alt={featuredImage.node.altText}
                          className="object-contain object-center"
                        />
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <Swiper
                slidesPerView={1.3}
                loop={true}
                grabCursor={true}
                autoplay={{
                  delay: 4000
                }}
                breakpoints={{
                  600: {
                    slidesPerView: 4
                  }
                }}
              >
                {clientes &&
                  clientes.nodes.map(({ featuredImage }, index) => (
                    <SwiperSlide key={index}>
                      <div className="flex justify-center items-center h-16 md:h-20 md:w-48">
                        <GatsbyImage
                          image={
                            featuredImage.node.localFile.childImageSharp
                              .gatsbyImageData
                          }
                          alt={featuredImage.node.altText}
                          className="object-contain object-center"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
              </Swiper>
            )}
          </div>
        </div>
      </FullPageSection>
    </div>
  )
}

export default Clients
